/**
 * OCP Jacket Uniform Builder
 * Bradley's Surplus — Custom Shopify Section
 *
 * Cart strategy: All items (jacket, tapes, rank, badges) are added as individual
 * line items via the Shopify AJAX Cart API (/cart/add.js). Sewing is ALWAYS
 * included for every tape, rank, and badge — it's not a customer-facing option.
 * One "Sewing Service" line item is added to the cart for each sewn item so the
 * in-house team's labor is accurately captured in the order.
 *
 * Required Shopify setup:
 *  1. Create a "Sewing Service" product at $5.00 (handle: sewing-service)
 *  2. Create collections: ranks, skill-badges, id-ng-badges
 *  3. Set section settings in the Shopify theme editor to match your product/collection handles
 */

(function () {
  'use strict';

  // ─── Badge Layout Definitions ──────────────────────────────────────────────
  // grid: array of rows, each row is an array of slot numbers (displayed in that arrangement)
  // positions: map of slot number → human-readable position (e.g. "Bottom Left").
  //            Used on the customer summary AND written to the cart line-item
  //            properties so whoever is sewing the jacket can see exactly which
  //            badge goes where without having to decode "Slot 1 of 4".
  const BADGE_LAYOUTS = [
    { id: 1, slots: 1, label: '1 Badge',          grid: [[1]],
      positions: { 1: 'Center' } },
    { id: 2, slots: 2, label: '2 Badges',          grid: [[2], [1]],
      positions: { 1: 'Bottom', 2: 'Top' } },
    { id: 3, slots: 3, label: '3 Badges',          grid: [[3], [1, 2]],
      positions: { 1: 'Bottom Left', 2: 'Bottom Right', 3: 'Top Center' } },
    { id: 4, slots: 3, label: '3 Badges (Stack)',  grid: [[3], [2], [1]],
      positions: { 1: 'Bottom', 2: 'Middle', 3: 'Top' } },
    { id: 5, slots: 4, label: '4 Badges (2×2)',    grid: [[3, 4], [1, 2]],
      positions: { 1: 'Bottom Left', 2: 'Bottom Right', 3: 'Top Left', 4: 'Top Right' } },
    { id: 6, slots: 4, label: '4 Badges',          grid: [[4], [3], [1, 2]],
      positions: { 1: 'Bottom Left', 2: 'Bottom Right', 3: 'Middle Center', 4: 'Top Center' } },
    { id: 7, slots: 5, label: '5 Badges',          grid: [[5], [3, 4], [1, 2]],
      positions: { 1: 'Bottom Left', 2: 'Bottom Right', 3: 'Middle Left', 4: 'Middle Right', 5: 'Top Center' } },
  ];

  // Helper: get a position name for a given layout + slot index (0-based).
  function positionForSlot(layout, slotIndex) {
    if (!layout || !layout.positions) return `Slot ${slotIndex + 1}`;
    const slotNumber = slotIndex + 1;
    return layout.positions[slotNumber] || `Slot ${slotNumber}`;
  }

  // ─── Application State ─────────────────────────────────────────────────────
  let config = {};
  // Two jackets are offered (both used condition). The customer picks one in
  // Step 1, then configures the size/fit variant in Step 2. `jacketProducts`
  // holds both product records keyed by 'A' / 'B'; `getActiveJacketProduct()`
  // returns the currently-chosen one based on `state.jacketChoice`.
  let jacketProducts = { A: null, B: null };
  let sewingVariantId = null;
  let collections = { ranks: [], badges: [], idBadges: [] };

  // Per-grid pagination state, keyed by grid element id (e.g. 'rank-grid').
  // Preserved at module scope so that re-renders (triggered by selecting a
  // card, changing a count, etc.) don't lose the current page or search
  // filter. Cleared implicitly when the user leaves the step and comes back —
  // which is the right behaviour since the step HTML is rebuilt from scratch.
  const gridPageState = {};

  function getActiveJacketProduct() {
    return state.jacketChoice ? jacketProducts[state.jacketChoice] : null;
  }

  // ─── Stock Helpers ─────────────────────────────────────────────────────────
  // Shopify's /products/{handle}.js endpoint returns `available` on the product
  // (true when ANY variant is purchasable) and on every variant. A variant is
  // `available: false` when it's sold out AND the product is NOT set to
  // "continue selling when out of stock" — which is exactly the case we want to
  // block on. We check the variant array first (source of truth) and fall back
  // to the product-level flag.
  function inStockVariants(product) {
    if (!product || !Array.isArray(product.variants)) return [];
    return product.variants.filter(v => v.available !== false);
  }

  function isJacketInStock(product) {
    if (!product) return false;
    if (product.available === false) return false;
    return inStockVariants(product).length > 0;
  }

  // Is a given option value still reachable, given the customer's OTHER current
  // selections? Used to grey out sizes/fits in Step 2 that would resolve to a
  // sold-out variant. We deliberately ignore the selection in this same option
  // group so the customer can always switch values within a group.
  function isOptionValueAvailable(product, optIdx, value) {
    if (!product || !Array.isArray(product.variants)) return false;
    return product.variants.some(v => {
      if (v.available === false) return false;
      if (v['option' + (optIdx + 1)] !== value) return false;
      return Object.entries(state.selectedOptions).every(([i, val]) => {
        const idx = parseInt(i, 10);
        if (idx === optIdx) return true;
        return v['option' + (idx + 1)] === val;
      });
    });
  }

  // Returns the variant matching the current full option selection, or null if
  // the customer hasn't picked every option yet.
  function findMatchingVariant(product) {
    if (!product || !Array.isArray(product.variants)) return null;
    if (Object.keys(state.selectedOptions).length < product.options.length) return null;
    return product.variants.find(v =>
      Object.entries(state.selectedOptions).every(
        ([idx, val]) => v['option' + (parseInt(idx, 10) + 1)] === val
      )
    ) || null;
  }

  const state = {
    currentStep: 1,
    totalSteps: 6,
    selectedOptions: {},     // jacket option selections

    jacketChoice: null,      // 'A' | 'B' — which jacket product the customer picked

    jacket: { variantId: null, price: 0, title: '', variantTitle: '' },

    tape: {
      variantId: null,
      price: 0,
      customName: '',
    },

    rank: {
      variantId: null,
      price: 0,
      title: '',
      image: '',
    },

    badges: {
      wantBadges: null,    // null = unanswered, true/false
      layout: null,        // selected BADGE_LAYOUTS entry
      selections: [],      // array of { variantId, price, title, image } or null
    },

    idBadges: {
      wantBadges: null,
      count: 1,
      selections: [],      // array of { variantId, price, title, image }
    },
  };

  // ─── Initialization ────────────────────────────────────────────────────────
  async function init() {
    const container = document.getElementById('ocp-builder');
    if (!container) return;

    config = {
      jacketAHandle:      container.dataset.jacketAHandle     || 'ocp-jacket-used-a',
      jacketALabel:       container.dataset.jacketALabel      || '',
      jacketADesc:        container.dataset.jacketADesc       || '',
      jacketBHandle:      container.dataset.jacketBHandle     || 'ocp-jacket-used-b',
      jacketBLabel:       container.dataset.jacketBLabel      || '',
      jacketBDesc:        container.dataset.jacketBDesc       || '',
      nameTapeHandle:     container.dataset.nameTapeHandle    || 'name-tape-set',
      ranksHandle:        container.dataset.ranksHandle       || 'ranks',
      badgesHandle:       container.dataset.badgesHandle      || 'skill-badges',
      idBadgesHandle:     container.dataset.idBadgesHandle    || 'id-ng-badges',
      sewingHandle:       container.dataset.sewingHandle      || 'sewing-service',
      sewingFee:          parseFloat(container.dataset.sewingFee) || 5.00,
      // How many ranks/badges to show per page in the paginated grids. Set
      // via section settings; defaults to 8.
      gridPageSize:       Math.max(1, parseInt(container.dataset.gridPageSize) || 8),
      gridPageSizeMobile: Math.max(1, parseInt(container.dataset.gridPageSizeMobile) || 4),
    };

    showLoading(true);

    try {
      await Promise.all([
        loadJacketProducts(),
        loadAllCollections(),
        loadSewingService(),
        loadTapeProduct(),
      ]);
    } catch (e) {
      console.error('[OCP Builder] Init error:', e);
    }

    showLoading(false);
    renderCurrentStep();
    attachNavListeners();
  }

  // ─── Data Loading ──────────────────────────────────────────────────────────
  // Loads both jacket products in parallel so the picker in Step 1 can render
  // them side-by-side with real titles, images, and starting prices. If one
  // product fails to load, the other is still usable and the picker card for
  // the failed product shows an error state instead of breaking the whole flow.
  async function loadJacketProducts() {
    const fetchOne = async (handle) => {
      if (!handle) return null;
      try {
        const resp = await fetch(`/products/${handle}.js`);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        return await resp.json();
      } catch (e) {
        console.error(`[OCP Builder] Failed to load jacket "${handle}":`, e);
        return null;
      }
    };
    const [a, b] = await Promise.all([
      fetchOne(config.jacketAHandle),
      fetchOne(config.jacketBHandle),
    ]);
    jacketProducts.A = a;
    jacketProducts.B = b;
  }

  async function loadAllCollections() {
    const [ranks, badges, idBadges] = await Promise.all([
      fetchCollectionProducts(config.ranksHandle),
      fetchCollectionProducts(config.badgesHandle),
      fetchCollectionProducts(config.idBadgesHandle),
    ]);
    collections.ranks    = ranks;
    collections.badges   = badges;
    collections.idBadges = idBadges;
  }

  async function fetchCollectionProducts(handle) {
    try {
      const resp = await fetch(`/collections/${handle}/products.json?limit=250`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      const products = data.products || [];

      // ⚠️ IMPORTANT: Shopify's /collections/{handle}/products.json endpoint
      // returns prices as DOLLAR STRINGS (e.g. "4.95"), unlike /products/{handle}.js
      // which returns prices as INTEGER CENTS (e.g. 495). Normalize everything
      // to integer cents so the rest of the code can treat prices consistently.
      products.forEach(p => {
        if (Array.isArray(p.variants)) {
          p.variants.forEach(v => {
            // If price is a string, it's in dollars — convert to cents.
            if (typeof v.price === 'string') {
              v.price = Math.round(parseFloat(v.price) * 100);
            }
            if (typeof v.compare_at_price === 'string') {
              v.compare_at_price = Math.round(parseFloat(v.compare_at_price) * 100);
            }
          });
        }
      });

      return products;
    } catch (e) {
      console.error(`[OCP Builder] Failed to load collection "${handle}":`, e);
      return [];
    }
  }

  async function loadSewingService() {
    try {
      const resp = await fetch(`/products/${config.sewingHandle}.js`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const product = await resp.json();
      if (product.variants && product.variants.length > 0) {
        sewingVariantId = product.variants[0].id;
      }
    } catch (e) {
      console.error('[OCP Builder] Failed to load sewing service product:', e);
    }
  }

  // ─── Step Rendering ────────────────────────────────────────────────────────
  function renderCurrentStep() {
    const content = document.getElementById('builder-content');
    if (!content) return;

    content.innerHTML = '';

    switch (state.currentStep) {
      case 1: renderJacketChooserStep(content); break;
      case 2: renderJacketStep(content);        break;
      case 3: renderTapeStep(content);          break;
      case 4: renderRankStep(content);          break;
      case 5: renderBadgesStep(content);        break;
      case 6: renderIdBadgesStep(content);      break;
      case 7: renderSummaryStep(content);       break;
    }

    updateProgressBar();
    updateNavButtons();
  }

  // Step 1 — Pick which of the two jacket products to build on.
  // Both are "used" condition; the differentiator is configured via the
  // display label + short description in the section settings. Choosing a
  // jacket here sets `state.jacketChoice` and resets any previously-picked
  // variant/options (so switching jackets doesn't leave stale data behind).
  function renderJacketChooserStep(content) {
    const a = jacketProducts.A;
    const b = jacketProducts.B;

    // Starting price for each jacket — min across its variants so the picker
    // card can show "from $XX.XX". Returns null if product isn't loaded.
    function startingPrice(product) {
      if (!product || !Array.isArray(product.variants) || product.variants.length === 0) return null;
      // Quote the cheapest variant the customer can actually buy. Falls back to
      // all variants if nothing is in stock (the card will say "Out of Stock").
      const pool = inStockVariants(product).length ? inStockVariants(product) : product.variants;
      const min = pool.reduce((m, v) => (v.price < m ? v.price : m), pool[0].price);
      return min / 100;
    }

    function renderCard(key, product, labelOverride, desc) {
      const loaded = !!product;
      const title  = labelOverride || product?.title || (key === 'A' ? 'Jacket A' : 'Jacket B');
      // Shopify's /products/{handle}.js endpoint returns `featured_image` as
      // a string URL and `images` as an array of string URLs (NOT objects with
      // `.src`, unlike the /collections/{handle}/products.json endpoint used
      // elsewhere). Prefer featured_image; fall back to images[0] as a string.
      const img    = product?.featured_image || product?.images?.[0] || '';
      const price  = startingPrice(product);
      const isSelected = state.jacketChoice === key;

      if (!loaded) {
        return `
          <div class="jacket-choice-card jacket-choice-card--error" aria-disabled="true">
            <div class="jacket-choice-body">
              <h3 class="jacket-choice-title">${escapeAttr(title)}</h3>
              <p class="jacket-choice-desc">This jacket couldn't be loaded. Check the product handle in the theme editor.</p>
            </div>
          </div>
        `;
      }

      // ── Out of stock ─────────────────────────────────────────────────────
      // Tell the customer HERE, on step 1, rather than letting them build a
      // full uniform and hit a wall at checkout. The card is rendered as a
      // non-interactive div (not a button) so it can't be selected at all, and
      // links out to the product page where a back-in-stock app can capture
      // the email.
      if (!isJacketInStock(product)) {
        return `
          <div class="jacket-choice-card jacket-choice-card--soldout" aria-disabled="true">
            <div class="jacket-choice-img-wrap">
              ${img
                ? `<img src="${escapeAttr(img)}" alt="" class="jacket-choice-img" loading="lazy" />`
                : '<div class="jacket-choice-no-img" aria-hidden="true">No Image</div>'
              }
              <div class="jacket-choice-soldout-pill">Out of Stock</div>
            </div>
            <div class="jacket-choice-body">
              <h3 class="jacket-choice-title">${escapeAttr(title)}</h3>
              <p class="jacket-choice-soldout-note">
                This jacket is currently out of stock, so it can't be customized right now.
              </p>
              ${product.handle
                ? `<a class="jacket-choice-soldout-link" href="/products/${escapeAttr(product.handle)}">Notify me when it's back →</a>`
                : ''
              }
            </div>
          </div>
        `;
      }

      return `
        <button
          type="button"
          class="jacket-choice-card ${isSelected ? 'selected' : ''}"
          data-jacket-key="${key}"
          aria-pressed="${isSelected ? 'true' : 'false'}"
          aria-label="${escapeAttr(title)}${price != null ? `, from ${formatMoney(price)}` : ''}${isSelected ? ', selected' : ''}"
        >
          <div class="jacket-choice-img-wrap">
            ${img
              ? `<img src="${escapeAttr(img)}" alt="" class="jacket-choice-img" loading="lazy" />`
              : '<div class="jacket-choice-no-img" aria-hidden="true">No Image</div>'
            }
            ${isSelected ? '<div class="jacket-choice-check" aria-hidden="true">✓</div>' : ''}
          </div>
          <div class="jacket-choice-body">
            <h3 class="jacket-choice-title">${escapeAttr(title)}</h3>
            ${desc ? `<p class="jacket-choice-desc">${escapeAttr(desc)}</p>` : ''}
            ${price != null ? `<p class="jacket-choice-price">From ${formatMoney(price)}</p>` : ''}
          </div>
        </button>
      `;
    }

    // If neither jacket can be bought there is nothing to build — say so up
    // front and keep the Next button locked (see isStepValid case 1).
    const aInStock = isJacketInStock(a);
    const bInStock = isJacketInStock(b);
    const noneAvailable = !aInStock && !bInStock;
    const someAvailable = aInStock !== bInStock;

    let stockNotice = '';
    if (noneAvailable) {
      stockNotice = `
        <div class="builder-stock-notice builder-stock-notice--blocking" role="alert">
          <span class="builder-stock-notice-icon" aria-hidden="true">⚠️</span>
          <div>
            <strong>Both jackets are currently out of stock.</strong>
            <p>We can't start a build right now. Check back soon, or
               <a href="/pages/contact">contact us</a> and we'll let you know the
               moment more stock lands.</p>
          </div>
        </div>
      `;
    } else if (someAvailable) {
      stockNotice = `
        <div class="builder-stock-notice" role="status">
          <span class="builder-stock-notice-icon" aria-hidden="true">ℹ️</span>
          <div>
            <p>One of our jacket styles is temporarily out of stock. The available
               style is shown below and ready to customize.</p>
          </div>
        </div>
      `;
    }

    content.innerHTML = `
      <div class="step-header">
        <h2 class="step-title">Choose Your Jacket</h2>
        <p class="step-desc">Both jackets are used condition. Pick the one you'd like to customize — you'll select size and fit on the next step.</p>
      </div>

      ${stockNotice}

      <div class="jacket-chooser" role="radiogroup" aria-label="Choose your jacket">
        ${renderCard('A', a, config.jacketALabel, config.jacketADesc)}
        ${renderCard('B', b, config.jacketBLabel, config.jacketBDesc)}
      </div>
    `;

    content.querySelectorAll('.jacket-choice-card[data-jacket-key]').forEach(btn => {
      btn.addEventListener('click', function () {
        const key = this.dataset.jacketKey;
        if (!key || key === state.jacketChoice) return;
        // Belt-and-braces: sold-out jackets render as non-clickable divs, but
        // guard here too so a stale DOM / keyboard activation can't slip past.
        if (!isJacketInStock(jacketProducts[key])) {
          flashMessage('That jacket is currently out of stock.');
          return;
        }
        onJacketChoiceChange(key);
      });
    });
  }

  // Switching which jacket the customer is building invalidates any
  // previously-picked variant/options (they belong to the OTHER product).
  // Clear them here so the user can't accidentally carry a Small/Regular
  // variant from Jacket A into Jacket B, and so Step 2 re-renders fresh.
  function onJacketChoiceChange(key) {
    state.jacketChoice = key;
    state.selectedOptions = {};
    state.jacket = { variantId: null, price: 0, title: '', variantTitle: '' };
    renderCurrentStep();
  }

  // Step 2 — Jacket Variant Selection (size / fit for the chosen jacket)
  function renderJacketStep(content) {
    const jacketProduct = getActiveJacketProduct();
    if (!jacketProduct) {
      content.innerHTML = `<div class="builder-error">
        Unable to load jacket product. Please <a href="javascript:location.reload()">refresh the page</a>.
      </div>`;
      return;
    }

    // Sizes / fits that would resolve to a sold-out variant are rendered
    // disabled with a strike-through so the customer never picks a combination
    // we can't actually ship.
    const optionsHtml = jacketProduct.options.map((opt, i) => {
      const groupId = `jacket-opt-${i}`;
      return `
      <div class="option-group" role="group" aria-labelledby="${groupId}">
        <div class="option-group-label" id="${groupId}">${opt.name}</div>
        <div class="option-values">
          ${opt.values.map(val => {
            const isSelected  = state.selectedOptions[i] === val;
            const isAvailable = isOptionValueAvailable(jacketProduct, i, val);
            const isDisabled  = !isAvailable && !isSelected;
            return `
            <button
              type="button"
              class="option-chip ${isSelected ? 'selected' : ''} ${isDisabled ? 'unavailable' : ''}"
              data-option-idx="${i}"
              data-value="${val}"
              ${isDisabled ? 'disabled aria-disabled="true"' : ''}
              aria-pressed="${isSelected ? 'true' : 'false'}"
              aria-label="${escapeAttr(opt.name + ' ' + val)}${isDisabled ? ', out of stock' : ''}"
              title="${isDisabled ? 'Out of stock' : escapeAttr(val)}"
            >${val}</button>
          `;
          }).join('')}
        </div>
      </div>
    `;
    }).join('');

    // Fallback message for the (rare) case where a fully-selected combination
    // is unavailable — e.g. state restored from an earlier render.
    const currentMatch = findMatchingVariant(jacketProduct);
    const comboSoldOut = !!currentMatch && currentMatch.available === false;

    // See note in renderJacketChooserStep: /products/{handle}.js returns image
    // URLs as strings and exposes the main image as `featured_image`.
    const imageUrl = jacketProduct.featured_image
      || (jacketProduct.images && jacketProduct.images[0])
      || '';

    content.innerHTML = `
      <div class="step-header">
        <h2 class="step-title">Jacket Fit</h2>
        <p class="step-desc">Select the size and style that fits you.</p>
      </div>
      <div class="jacket-layout">
        ${imageUrl ? `<div class="jacket-image-wrap"><img src="${imageUrl}" alt="${jacketProduct.title}" class="jacket-image" /></div>` : ''}
        <div class="jacket-options-wrap">
          <h3 class="product-name">${jacketProduct.title}</h3>
          <div class="jacket-options">${optionsHtml}</div>
          <div id="variant-display" class="variant-display ${state.jacket.variantId ? '' : 'hidden'}">
            <div class="variant-display-inner">
              <span class="variant-label">Selected:</span>
              <span id="variant-title-text">${state.jacket.variantTitle}</span>
              <span id="variant-price-text" class="variant-price">${state.jacket.price ? formatMoney(state.jacket.price) : ''}</span>
            </div>
          </div>
          ${comboSoldOut ? `
            <p class="jacket-combo-soldout" role="alert">
              ⚠️ ${escapeAttr(jacketProduct.title)} — ${escapeAttr(currentMatch.title)} is out of stock.
              Please choose a different size or fit above.
            </p>
          ` : ''}
          ${!state.jacket.variantId && !comboSoldOut ? '<p class="select-prompt">↑ Select all options above to continue</p>' : ''}
        </div>
      </div>
    `;

    // Restore previous selections visually
    Object.entries(state.selectedOptions).forEach(([idx, val]) => {
      const btn = content.querySelector(`.option-chip[data-option-idx="${idx}"][data-value="${val}"]`);
      if (btn) btn.classList.add('selected');
    });

    // Attach option chip events
    content.querySelectorAll('.option-chip').forEach(btn => {
      btn.addEventListener('click', function () {
        if (this.disabled) return;
        const idx = this.dataset.optionIdx;
        const val = this.dataset.value;

        // Deselect siblings
        content.querySelectorAll(`.option-chip[data-option-idx="${idx}"]`)
          .forEach(b => {
            b.classList.remove('selected');
            b.setAttribute('aria-pressed', 'false');
          });
        this.classList.add('selected');
        this.setAttribute('aria-pressed', 'true');

        state.selectedOptions[idx] = val;
        resolveJacketVariant();
        // Re-render so the OTHER option groups recalculate which values are
        // still in stock given this new selection.
        renderCurrentStep();
      });
    });
  }

  function resolveJacketVariant() {
    const jacketProduct = getActiveJacketProduct();
    if (!jacketProduct) return;
    const numOptions = jacketProduct.options.length;
    if (Object.keys(state.selectedOptions).length < numOptions) {
      state.jacket.variantId = null;
      updateNavButtons();
      return;
    }

    const match = jacketProduct.variants.find(v =>
      Object.entries(state.selectedOptions).every(
        ([idx, val]) => v[`option${parseInt(idx) + 1}`] === val
      )
    );

    // A matched-but-sold-out variant is treated as no match: we never let a
    // sold-out variant into state, so Step 2 validation blocks the flow.
    if (match && match.available !== false) {
      state.jacket.variantId   = match.id;
      state.jacket.price       = match.price / 100;
      state.jacket.variantTitle = match.title;
      state.jacket.title       = `${jacketProduct.title} — ${match.title}`;

      const display = document.getElementById('variant-display');
      const titleEl = document.getElementById('variant-title-text');
      const priceEl = document.getElementById('variant-price-text');

      if (display) {
        display.classList.remove('hidden');
        titleEl.textContent = match.title;
        priceEl.textContent = formatMoney(match.price / 100);
      }

      // Remove "select prompt" if present
      const prompt = document.querySelector('.select-prompt');
      if (prompt) prompt.remove();
    } else {
      state.jacket.variantId = null;
    }

    updateNavButtons();
  }

  // Step 2 — Name Tapes
  function renderTapeStep(content) {
    content.innerHTML = `
      <div class="step-header">
        <h2 class="step-title">Name Tapes</h2>
        <p class="step-desc">Both the U.S. Army tape and your custom name tape are required for all uniform builds.</p>
      </div>

      <div class="tape-cards">
        <div class="tape-card included-card">
          <div class="tape-card-icon" aria-hidden="true">🇺🇸</div>
          <div class="tape-card-body">
            <div class="tape-card-title">U.S. Army Tape</div>
            <div class="tape-card-note">Standard service tape — part of the name tape set</div>
          </div>
        </div>

        <div class="tape-card name-tape-card">
          <div class="tape-card-icon" aria-hidden="true">✏️</div>
          <div class="tape-card-body">
            <label for="custom-name-input" class="tape-card-title">
              Custom Name Tape
              <span class="required-star" aria-hidden="true">*</span>
              <span class="sr-only">(required)</span>
            </label>
            <div class="tape-card-note" id="custom-name-hint">Your last name as sewn on the uniform (ALL CAPS)</div>
            <div class="name-input-group">
              <input
                type="text"
                id="custom-name-input"
                class="name-input"
                placeholder="e.g. SMITH"
                maxlength="20"
                autocomplete="off"
                aria-required="true"
                aria-describedby="custom-name-hint custom-name-format-hint"
                value="${state.tape.customName}"
              />
              <span class="name-char-count">
                <span class="sr-only">Character count: </span>
                <span id="name-char-count">${state.tape.customName.length}</span>
                <span aria-hidden="true">/20</span>
                <span class="sr-only">of 20 allowed</span>
              </span>
            </div>
            <p class="input-hint" id="custom-name-format-hint">Letters only, will appear in ALL CAPS on the tape</p>
          </div>
        </div>
      </div>

      <div class="tape-price-row">
        <div class="tape-price-label">
          Name Tapes Total
          <span class="tape-price-sub">Sewing is already included in this price</span>
        </div>
        <div class="tape-price-value" id="tape-price-display">
          ${state.tape.variantId
            ? formatMoney(state.tape.price)
            : '<span class="loading-dots">Loading…</span>'}
        </div>
      </div>
      ${!state.tape.variantId ? `
        <div class="builder-error" style="margin-top: 12px;">
          ⚠️ The Name Tape product could not be loaded. Check that a product exists
          at handle <strong>${config.nameTapeHandle}</strong> in your Shopify admin
          and that it has a published variant.
        </div>
      ` : ''}
    `;

    // Retry loading the tape product if it wasn't loaded during init
    // (e.g. transient network error). This ensures step 2 always has a price.
    if (!state.tape.variantId) loadTapeProduct();

    const nameInput = document.getElementById('custom-name-input');
    const charCount = document.getElementById('name-char-count');

    if (nameInput) {
      nameInput.addEventListener('input', function () {
        this.value = this.value.toUpperCase().replace(/[^A-Z\s\-]/g, '');
        state.tape.customName = this.value.trim();
        if (charCount) charCount.textContent = this.value.length;
        updateNavButtons();
      });
    }
  }

  async function loadTapeProduct() {
    try {
      const resp = await fetch(`/products/${config.nameTapeHandle}.js`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const product = await resp.json();
      if (product.variants && product.variants.length > 0) {
        state.tape.variantId = product.variants[0].id;
        // /products/{handle}.js returns price in integer cents.
        // The Name Tape Set product price already includes sewing for both
        // the U.S. Army tape and the custom name tape, so no extra fee is added.
        state.tape.price     = product.variants[0].price / 100;
        const display = document.getElementById('tape-price-display');
        if (display) display.textContent = formatMoney(state.tape.price);
        // Refresh Next-button state — step 2 validity depends on variantId.
        updateNavButtons();
      } else {
        throw new Error('Name Tape product has no published variants.');
      }
    } catch (e) {
      console.error('[OCP Builder] Failed to load tape product:', e);
      const display = document.getElementById('tape-price-display');
      if (display) display.textContent = 'Unavailable';
    }
  }

  // Step 3 — Rank Selection
  function renderRankStep(content) {
    content.innerHTML = `
      <div class="step-header">
        <h2 class="step-title">Sew-On Rank</h2>
        <p class="step-desc">Choose your rank. Professional in-house sewing is included with every uniform build.</p>
      </div>

      <div class="search-bar-wrap">
        <span class="search-icon" aria-hidden="true">🔍</span>
        <label for="rank-search" class="sr-only">Search ranks</label>
        <input type="text" id="rank-search" class="search-bar" placeholder="Search ranks…" autocomplete="off" aria-label="Search ranks" />
      </div>

      <div class="product-grid" id="rank-grid"></div>
      <div class="product-pager-wrap" id="rank-pager"></div>

      ${state.rank.variantId ? buildSewIncludedPanel(state.rank.title) : ''}
    `;

    setupPaginatedGrid({
      gridId:            'rank-grid',
      pagerId:           'rank-pager',
      searchInputId:     'rank-search',
      allProducts:       collections.ranks,
      type:              'rank',
      onSelect:          onRankSelect,
      selectedVariantId: state.rank.variantId,
      focusVariantId:    state.rank.variantId,
    });
  }

  function onRankSelect(product, variant) {
    state.rank.variantId = variant.id;
    state.rank.price     = variant.price / 100;
    state.rank.title     = product.title;
    state.rank.image     = product.images[0]?.src || '';
    renderCurrentStep();
  }

  // Small static panel shown once a sewable item is selected. Replaces the old
  // customer-facing sew toggle — sewing is mandatory, so the panel simply
  // confirms that sewing is included and surfaces the added fee.
  function buildSewIncludedPanel(itemLabel) {
    return `
      <div class="sew-on-panel sew-included-panel">
        <div class="sew-included-row">
          <span class="sew-included-icon" aria-hidden="true">✓</span>
          <div>
            <div class="sew-included-title">Sewing included</div>
            ${itemLabel ? `<p class="sew-selected-name">Selected: <strong>${itemLabel}</strong></p>` : ''}
            <p class="sew-toggle-note">Sewing includes a +${formatMoney(config.sewingFee)} fee.</p>
          </div>
        </div>
      </div>
    `;
  }

  // Step 4 — Skill Badges
  function renderBadgesStep(content) {
    // Sub-state A: Yes/No question
    if (state.badges.wantBadges === null) {
      content.innerHTML = `
        <div class="step-header">
          <h2 class="step-title">Skill Badges</h2>
          <p class="step-desc">Would you like to add skill badges to your jacket?</p>
        </div>
        <div class="yn-buttons">
          <button class="yn-btn yn-yes" id="badges-yes">
            <span class="yn-icon" aria-hidden="true">✓</span>
            Yes, add skill badges
          </button>
          <button class="yn-btn yn-no" id="badges-no">
            <span class="yn-icon" aria-hidden="true">▶</span>
            No, skip this step
          </button>
        </div>
      `;
      document.getElementById('badges-yes').addEventListener('click', () => {
        state.badges.wantBadges = true;
        renderCurrentStep();
      });
      document.getElementById('badges-no').addEventListener('click', () => {
        state.badges.wantBadges  = false;
        state.badges.layout      = null;
        state.badges.selections  = [];
        renderCurrentStep();
      });
      return;
    }

    // Sub-state B: Choose layout
    if (state.badges.wantBadges && !state.badges.layout) {
      content.innerHTML = `
        <div class="step-header">
          <h2 class="step-title">Skill Badge Layout</h2>
          <p class="step-desc">How many skill badges, and in what arrangement?</p>
        </div>
        <div class="layout-grid" role="group" aria-label="Skill badge layout options">
          ${BADGE_LAYOUTS.map(layout => {
            const isSelected = state.badges.layout?.id === layout.id;
            return `
            <button type="button"
                    class="layout-btn ${isSelected ? 'selected' : ''}"
                    data-layout-id="${layout.id}"
                    title="${layout.label}"
                    aria-label="${layout.label}"
                    aria-pressed="${isSelected ? 'true' : 'false'}">
              <div class="layout-icon" aria-hidden="true">${buildLayoutIcon(layout)}</div>
              <span class="layout-btn-label">${layout.label}</span>
            </button>
          `;
          }).join('')}
        </div>
        <button class="text-link" id="badges-change-mind">← No badges after all</button>
      `;

      document.querySelectorAll('.layout-btn').forEach(btn => {
        btn.addEventListener('click', function () {
          const id     = parseInt(this.dataset.layoutId);
          const layout = BADGE_LAYOUTS.find(l => l.id === id);
          state.badges.layout     = layout;
          state.badges.selections = new Array(layout.slots).fill(null);
          renderCurrentStep();
        });
      });

      document.getElementById('badges-change-mind').addEventListener('click', () => {
        state.badges.wantBadges = null;
        renderCurrentStep();
      });
      return;
    }

    // Sub-state C: Select a badge for each slot
    if (state.badges.wantBadges && state.badges.layout) {
      const layout    = state.badges.layout;
      const slotsHtml = state.badges.selections.map((sel, i) => buildSlotCard(sel, i, 'badge', layout)).join('');

      content.innerHTML = `
        <div class="step-header">
          <h2 class="step-title">Select Skill Badges</h2>
          <p class="step-desc">Click a badge below to fill each slot. Professional sewing on every badge is included.</p>
        </div>

        <div class="layout-summary">
          <div class="layout-icon layout-icon-sm">${buildLayoutIcon(layout)}</div>
          <span>${layout.label}</span>
          <button class="text-link" id="change-layout-btn">Change layout</button>
        </div>

        <div class="jacket-schematic-wrap">
          <div class="jacket-schematic-label">Your jacket layout (live preview)</div>
          ${buildJacketSchematic(layout, state.badges.selections)}
        </div>

        <div class="badge-slot-list" id="badge-slot-list">
          ${slotsHtml}
        </div>

        <div class="search-bar-wrap">
          <span class="search-icon" aria-hidden="true">🔍</span>
          <label for="badge-search" class="sr-only">Search skill badges</label>
          <input type="text" id="badge-search" class="search-bar" placeholder="Search skill badges…" autocomplete="off" aria-label="Search skill badges" />
        </div>

        <p class="grid-hint">Click a badge to assign it to the next empty slot.</p>
        <div class="product-grid" id="badge-grid"></div>
        <div class="product-pager-wrap" id="badge-pager"></div>
      `;

      document.getElementById('change-layout-btn').addEventListener('click', () => {
        state.badges.layout     = null;
        state.badges.selections = [];
        renderCurrentStep();
      });

      // Skill badges must be unique across slots — the same badge can't be
      // used twice. Collect the variant ids already assigned and pass them
      // through so the grid renders those cards as "In use" (disabled).
      const usedBadgeVariantIds = state.badges.selections
        .filter(Boolean)
        .map(sel => sel.variantId);

      setupPaginatedGrid({
        gridId:         'badge-grid',
        pagerId:        'badge-pager',
        searchInputId:  'badge-search',
        allProducts:    collections.badges,
        type:           'badge',
        onSelect:       onBadgeSelect,
        usedVariantIds: usedBadgeVariantIds,
      });
      bindSlotRemoveButtons('badge');
    }
  }

  function onBadgeSelect(product, variant) {
    const nextEmpty = state.badges.selections.indexOf(null);
    if (nextEmpty === -1) {
      flashMessage('All badge slots are filled. Remove one to swap it out.');
      return;
    }

    // Enforce uniqueness — each skill badge can only occupy one slot. The
    // grid disables already-used cards visually, but this guard catches any
    // edge case (keyboard activation on stale DOM, race between re-renders,
    // etc.) so we never end up with duplicate badges in state.
    const alreadyUsed = state.badges.selections.some(
      sel => sel && sel.variantId === variant.id
    );
    if (alreadyUsed) {
      flashMessage(`“${product.title}” is already in another slot. Remove it first if you want to move it.`);
      return;
    }

    state.badges.selections[nextEmpty] = {
      variantId: variant.id,
      price:     variant.price / 100,
      title:     product.title,
      image:     product.images[0]?.src || '',
    };
    renderCurrentStep();
  }

  // Step 5 — ID / National Guard Badges
  function renderIdBadgesStep(content) {
    // Sub-state A: Yes/No
    if (state.idBadges.wantBadges === null) {
      content.innerHTML = `
        <div class="step-header">
          <h2 class="step-title">ID / National Guard Badges</h2>
          <p class="step-desc">Do you need any Identification or National Guard badges added?</p>
        </div>
        <div class="yn-buttons">
          <button class="yn-btn yn-yes" id="idbadges-yes">
            <span class="yn-icon" aria-hidden="true">✓</span>
            Yes, add ID/NG badges
          </button>
          <button class="yn-btn yn-no" id="idbadges-no">
            <span class="yn-icon" aria-hidden="true">▶</span>
            No, skip this step
          </button>
        </div>
      `;
      document.getElementById('idbadges-yes').addEventListener('click', () => {
        state.idBadges.wantBadges = true;
        renderCurrentStep();
      });
      document.getElementById('idbadges-no').addEventListener('click', () => {
        state.idBadges.wantBadges = false;
        state.idBadges.selections = [];
        renderCurrentStep();
      });
      return;
    }

    // Sub-state B: Select count + badges
    if (state.idBadges.wantBadges) {
      const slotsHtml = Array.from({ length: state.idBadges.count }, (_, i) =>
        buildSlotCard(state.idBadges.selections[i] || null, i, 'idbadge')
      ).join('');

      content.innerHTML = `
        <div class="step-header">
          <h2 class="step-title">ID / National Guard Badges</h2>
          <p class="step-desc">Choose your ID or National Guard badge. Price includes sewing.</p>
        </div>

        <div class="idbadge-note" role="note">
          <span class="idbadge-note-icon" aria-hidden="true">ℹ️</span>
          <p>
            Sewing an ID/NG badge onto an OCP uniform often means attaching it through
            the pocket and uniform fabric. This secures the badge for a professional
            display, but it also typically makes the pocket mostly unusable for storage.
          </p>
        </div>

        <div class="count-selector">
          <span class="count-label" id="idbadge-count-label">How many badges?</span>
          <div class="count-btns" role="group" aria-labelledby="idbadge-count-label">
            <button type="button" class="count-btn ${state.idBadges.count === 1 ? 'active' : ''}"
                    data-count="1"
                    aria-pressed="${state.idBadges.count === 1 ? 'true' : 'false'}">1 Badge</button>
            <button type="button" class="count-btn ${state.idBadges.count === 2 ? 'active' : ''}"
                    data-count="2"
                    aria-pressed="${state.idBadges.count === 2 ? 'true' : 'false'}">2 Badges</button>
          </div>
        </div>

        <div class="badge-slot-list" id="idbadge-slot-list">
          ${slotsHtml}
        </div>

        <div class="search-bar-wrap">
          <span class="search-icon" aria-hidden="true">🔍</span>
          <label for="idbadge-search" class="sr-only">Search ID and National Guard badges</label>
          <input type="text" id="idbadge-search" class="search-bar" placeholder="Search ID/NG badges…" autocomplete="off" aria-label="Search ID and National Guard badges" />
        </div>

        <p class="grid-hint">Click a badge to assign it to the next empty slot.</p>
        <div class="product-grid" id="idbadge-grid"></div>
        <div class="product-pager-wrap" id="idbadge-pager"></div>
      `;

      // Count selector
      content.querySelectorAll('.count-btn').forEach(btn => {
        btn.addEventListener('click', function () {
          const newCount = parseInt(this.dataset.count);
          state.idBadges.count      = newCount;
          state.idBadges.selections = state.idBadges.selections.slice(0, newCount);
          renderCurrentStep();
        });
      });

      setupPaginatedGrid({
        gridId:        'idbadge-grid',
        pagerId:       'idbadge-pager',
        searchInputId: 'idbadge-search',
        allProducts:   collections.idBadges,
        type:          'idbadge',
        onSelect:      onIdBadgeSelect,
      });
      bindSlotRemoveButtons('idbadge');
    }
  }

  function onIdBadgeSelect(product, variant) {
    if (state.idBadges.selections.length >= state.idBadges.count) {
      flashMessage(`You've already selected ${state.idBadges.count} badge(s). Remove one to swap it out.`);
      return;
    }
    state.idBadges.selections.push({
      variantId: variant.id,
      price:     variant.price / 100,
      title:     product.title,
      image:     product.images[0]?.src || '',
    });
    renderCurrentStep();
  }

  // Step 6 — Summary + Add to Cart
  function renderSummaryStep(content) {
    const lineItems = buildSummaryLineItems();
    const total     = lineItems.reduce((sum, item) => sum + item.price, 0);

    const hasBadges = state.badges.wantBadges &&
                      state.badges.layout &&
                      state.badges.selections.some(Boolean);

    const schematicHtml = hasBadges ? `
      <div class="jacket-schematic-wrap summary-schematic">
        <div class="jacket-schematic-label">
          Sewing Layout — ${state.badges.layout.label}
        </div>
        ${buildJacketSchematic(state.badges.layout, state.badges.selections)}
        <p class="jacket-schematic-note">
          This diagram will appear on your order so the person sewing your jacket knows exactly which badge goes in each position.
        </p>
      </div>
    ` : '';

    // Master Build Summary preview — mirrors exactly what gets attached to the
    // jacket line item as order properties. Styled inline so this integration
    // doesn't require editing the CSS file.
    const master = buildMasterBuildSummary();
    const masterPreviewHtml = master.hasItems ? `
      <div class="master-summary-preview"
           style="background:#FBF7EC;border:1.5px solid #8F7A4A;border-radius:8px;padding:14px 16px;margin-bottom:16px;">
        <div style="display:flex;gap:10px;align-items:flex-start;margin-bottom:10px;">
          <span style="font-size:22px;line-height:1;" aria-hidden="true">📋</span>
          <div>
            <div style="font-weight:700;color:#4A5A2D;font-size:14px;letter-spacing:.2px;">
              Build Summary — shown on your order
            </div>
            <div style="font-size:12px;color:#6B5E3E;margin-top:2px;line-height:1.4;">
              Our sewing team sees this consolidated summary on your order so your entire build is visible at a glance — no hunting through line items.
            </div>
          </div>
        </div>
        <div style="font-size:12.5px;margin:8px 0 4px 0;color:#2A3419;">
          <strong style="color:#4A5A2D;">Assembly:</strong> Yes
        </div>
        <div style="font-size:12.5px;margin:4px 0;color:#2A3419;">
          <strong style="color:#4A5A2D;">Configuration:</strong> ${escapeXml(master.configuration)}
        </div>
        <div style="font-size:12.5px;color:#2A3419;">
          <strong style="color:#4A5A2D;">Build Summary:</strong>
          <div style="margin-top:6px;padding:8px 10px;background:#fff;border-left:3px solid #8F7A4A;border-radius:3px;">
            ${master.parts.map(p => `<div style="margin:3px 0;line-height:1.45;">${escapeXml(p)}</div>`).join('')}
          </div>
        </div>
      </div>
    ` : '';

    content.innerHTML = `
      <div class="step-header">
        <h2 class="step-title">Review Your Build</h2>
        <p class="step-desc">Everything looks good? Add it all to your cart below.</p>
      </div>

      ${masterPreviewHtml}

      ${schematicHtml}

      <div class="summary-list">
        ${lineItems.map(item => `
          <div class="summary-row">
            <div class="summary-row-left">
              ${item.image ? `<img src="${item.image}" alt="${item.name}" class="summary-thumb" />` : '<div class="summary-thumb-placeholder"></div>'}
              <div class="summary-row-info">
                <span class="summary-row-name">${item.name}</span>
                ${item.note ? `<span class="summary-row-note">${item.note}</span>` : ''}
              </div>
            </div>
            <span class="summary-row-price">${formatMoney(item.price)}</span>
          </div>
        `).join('')}
      </div>

      <div class="summary-total-row">
        <span class="summary-total-label">Estimated Total</span>
        <span class="summary-total-price">${formatMoney(total)}</span>
      </div>

      <div id="cart-message" class="cart-message hidden" role="alert" aria-live="assertive" aria-atomic="true"></div>

      <button class="add-to-cart-btn" id="add-to-cart-btn">
        Add to Cart — ${formatMoney(total)}
      </button>

      <p class="cart-footnote">
        Each item is tracked separately in your cart. Inventory availability is verified at checkout.
      </p>
    `;

    document.getElementById('add-to-cart-btn').addEventListener('click', addToCart);
  }

  function buildSummaryLineItems() {
    const items = [];

    // Jacket
    if (state.jacket.variantId) {
      const activeJacket = getActiveJacketProduct();
      items.push({
        name:  state.jacket.title,
        price: state.jacket.price,
        // /products/{handle}.js returns images as URL strings — see note in
        // renderJacketChooserStep. Use featured_image as the primary source.
        image: activeJacket?.featured_image || activeJacket?.images?.[0] || '',
      });
    }

    // Tapes — the Name Tape Set product price already includes sewing for
    // both tapes, so no extra Sewing Service line items are added here.
    if (state.tape.variantId) {
      items.push({
        name:  'U.S. Army Tape + Custom Name Tape',
        note:  `Name: ${state.tape.customName} · Sewing included`,
        price: state.tape.price,
        image: '',
      });
    }

    // Rank — sewing always included.
    if (state.rank.variantId) {
      items.push({
        name:  state.rank.title,
        price: state.rank.price,
        image: state.rank.image,
      });
      items.push({
        name:  `Sewing Service — ${state.rank.title}`,
        price: config.sewingFee,
        image: '',
      });
    }

    // Skill Badges — sewing always included on every badge.
    const layout = state.badges.layout;
    state.badges.selections.forEach((badge, i) => {
      if (!badge) return;
      const position = layout ? positionForSlot(layout, i) : '';
      const slotLabel = layout
        ? `Slot ${i + 1} of ${layout.slots} — ${position}`
        : `Slot ${i + 1}`;
      items.push({
        name:  badge.title,
        note:  `Position: ${slotLabel}`,
        price: badge.price,
        image: badge.image,
      });
      items.push({
        name:  `Sewing Service — ${slotLabel} (${badge.title})`,
        price: config.sewingFee,
        image: '',
      });
    });

    // ID / NG Badges — sewing always included.
    state.idBadges.selections.forEach((badge, i) => {
      items.push({
        name:  badge.title,
        price: badge.price,
        image: badge.image,
      });
      items.push({
        name:  `Sewing Service — ID Badge ${i + 1} (${badge.title})`,
        price: config.sewingFee,
        image: '',
      });
    });

    return items;
  }

  // ─── Master Build Summary ──────────────────────────────────────────────────
  // Produces a single consolidated summary of the entire uniform build,
  // formatted like the Unit Award Rack builder output so whoever is assembling
  // the jacket can read the full order from ONE line item (the jacket) without
  // having to scan through every child line item on the packing slip.
  //
  // The summary is attached as line-item properties ("Assembly", "Configuration",
  // "Build Summary") on the jacket itself. Shopify will surface these on:
  //   • the Shopify admin order detail page
  //   • order confirmation emails
  //   • Order Printer / Order Printer Pro packing slips
  //
  // Example output:
  //   Configuration: OCP Jacket — Medium / Regular
  //   Build Summary: 1. Name Tape: SMITH (U.S. Army + Custom, Sewn)
  //                | 2. Rank: Sergeant (E-5) [Sewn]
  //                | 3. Skill Badges (4 Badges 2×2):
  //                    [Top Left] Airborne, [Top Right] Air Assault,
  //                    [Bottom Left] CIB, [Bottom Right] Pathfinder [All Sewn]
  //                | 4. ID/NG Badges: Recruiter Badge, Drill Sergeant Badge [All Sewn]
  function buildMasterBuildSummary() {
    const parts = [];
    let n = 1;

    // 1. Name tapes (always present if they made it past step 2)
    if (state.tape.variantId && state.tape.customName) {
      parts.push(`${n}. Name Tape: ${state.tape.customName} (U.S. Army + Custom, Sewn)`);
      n++;
    }

    // 2. Rank
    if (state.rank.variantId) {
      parts.push(`${n}. Rank: ${state.rank.title} [Sewn]`);
      n++;
    }

    // 3. Skill badges — include layout label and per-slot position
    const layout = state.badges.layout;
    const filledBadges = state.badges.selections
      .map((b, i) => b ? {
        badge:    b,
        position: layout ? positionForSlot(layout, i) : `Slot ${i + 1}`,
      } : null)
      .filter(Boolean);

    if (filledBadges.length > 0) {
      const badgeList = filledBadges
        .map(({ badge, position }) => `[${position}] ${badge.title}`)
        .join(', ');
      const layoutLabel = layout ? layout.label : 'Custom';
      parts.push(`${n}. Skill Badges (${layoutLabel}): ${badgeList} [All Sewn]`);
      n++;
    }

    // 4. ID / National Guard badges
    if (state.idBadges.selections.length > 0) {
      const idList = state.idBadges.selections.map(b => b.title).join(', ');
      parts.push(`${n}. ID/NG Badges: ${idList} [All Sewn]`);
      n++;
    }

    return {
      configuration: state.jacket.title || 'OCP Jacket',
      buildSummary:  parts.length > 0
        ? parts.join(' | ')
        : 'Jacket only — no add-ons selected',
      parts:         parts,       // handy for line-by-line rendering in the UI preview
      hasItems:      parts.length > 0,
    };
  }

  // ─── Cart Integration ──────────────────────────────────────────────────────
  async function addToCart() {
    const btn = document.getElementById('add-to-cart-btn');
    const msg = document.getElementById('cart-message');

    if (btn) {
      btn.disabled     = true;
      btn.textContent  = 'Adding to cart…';
    }

    const cartItems = [];

    // Build the master summary once so it can be attached to the jacket (the
    // parent line item that the tailor will scan first) and referenced by
    // every child line item for cross-checking.
    const master = buildMasterBuildSummary();

    // Unique ID for this build — stamped on every line item as `_build_id`
    // (hidden from the customer thanks to the leading underscore). The
    // companion `ocp-cart-sync.js` script uses this ID to cascade-remove
    // child items when the parent jacket is removed from the cart.
    const buildId = 'ocp_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);

    // Index of the jacket inside cartItems so we can stamp `_build_role:
    // parent` on it later. -1 if no jacket (shouldn't happen — step 1
    // validation blocks the flow — but guarded anyway).
    let parentIdx = -1;

    // Jacket — carries the consolidated Build Summary so the tailor can read
    // the entire uniform order from this single line item on the packing slip.
    if (state.jacket.variantId) {
      parentIdx = cartItems.length;
      cartItems.push({
        id:         state.jacket.variantId,
        quantity:   1,
        properties: {
          'Assembly':      'Yes',
          'Configuration': master.configuration,
          'Build Summary': master.buildSummary,
          _builder:        'OCP Jacket Builder',
        },
      });
    }

    // Tape — sewing is already baked into the Name Tape Set product price,
    // so no separate Sewing Service line items are added for this step.
    if (state.tape.variantId) {
      cartItems.push({
        id:         state.tape.variantId,
        quantity:   1,
        properties: {
          'Custom Name':  state.tape.customName,
          'Includes':     'U.S. Army Tape + Custom Name Tape',
          'Sewing':       'Included in product price',
          _builder:       'OCP Jacket Builder',
        },
      });
    }

    // Rank — sewing always included.
    if (state.rank.variantId) {
      cartItems.push({
        id:         state.rank.variantId,
        quantity:   1,
        properties: {
          'Sewing':  'Included',
          _builder:  'OCP Jacket Builder',
        },
      });
      if (sewingVariantId) {
        cartItems.push({
          id:         sewingVariantId,
          quantity:   1,
          properties: {
            'Service For': `Rank — ${state.rank.title}`,
            _builder:      'OCP Jacket Builder',
          },
        });
      }
    }

    // Skill Badges — include layout + position so the sewer can read the
    // order and know exactly which badge goes in which spot. Sewing is
    // always included, so we add one sewing line item per badge.
    const layout = state.badges.layout;
    state.badges.selections.filter(Boolean).forEach((badge, i) => {
      const position = layout ? positionForSlot(layout, i) : `Slot ${i + 1}`;
      const slotLabel = layout
        ? `${i + 1} of ${layout.slots} — ${position}`
        : `${i + 1}`;
      cartItems.push({
        id:         badge.variantId,
        quantity:   1,
        properties: {
          'Layout':     layout ? layout.label : '—',
          'Position':   position,
          'Badge Slot': slotLabel,
          'Sewing':     'Included',
          _builder:     'OCP Jacket Builder',
        },
      });
      if (sewingVariantId) {
        cartItems.push({
          id:         sewingVariantId,
          quantity:   1,
          properties: {
            'Service For': `Skill Badge — ${position} (${badge.title})`,
            'Layout':      layout ? layout.label : '—',
            'Position':    position,
            _builder:      'OCP Jacket Builder',
          },
        });
      }
    });

    // ID / NG Badges — sewing always included.
    state.idBadges.selections.forEach((badge, i) => {
      cartItems.push({
        id:         badge.variantId,
        quantity:   1,
        properties: {
          'Badge Type': 'ID / National Guard',
          'Sewing':     'Included',
          _builder:     'OCP Jacket Builder',
        },
      });
      if (sewingVariantId) {
        cartItems.push({
          id:         sewingVariantId,
          quantity:   1,
          properties: {
            'Service For': `ID/NG Badge ${i + 1} — ${badge.title}`,
            _builder:      'OCP Jacket Builder',
          },
        });
      }
    });

    // Stamp every line item with the shared build ID + role. Doing this as a
    // post-processing pass (rather than inline in each push above) keeps the
    // diff small and guarantees no item gets missed. Properties prefixed with
    // `_` are automatically hidden from the customer on the cart and order
    // confirmation, but remain visible to the Shopify admin / sewing team.
    // Friendly label for the single merged cart line shown by the site-wide
    // uab-cart-clean.js script.
    const bundleTitle = 'Custom OCP Jacket Uniform'
      + (state.jacket.variantTitle ? ' (' + state.jacket.variantTitle + ')' : '');

    cartItems.forEach((item, idx) => {
      item.properties = Object.assign({}, item.properties || {}, {
        _build_id:   buildId,
        _build_role: idx === parentIdx ? 'parent' : 'child',
        // Cart-clean hooks: the shared uab-cart-clean.js groups every line with
        // the same `_Build ID` into ONE clean cart line titled `_Bundle Title`,
        // hides the individual rows, and adds a "Remove this build" button. All
        // items still go through to checkout / the order. (Same keys the unit
        // award builder uses, so no separate cart script is needed.)
        '_Build ID':     buildId,
        '_Bundle Title': bundleTitle,
      });
      // Mark sewing-service lines as fee items so they're folded away inside the
      // bundle (not listed under the summary's "View items").
      if (sewingVariantId && item.id === sewingVariantId) {
        item.properties['_fee_item'] = 'true';
      }
    });

    try {
      const resp = await fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body:    JSON.stringify({ items: cartItems }),
      });

      const data = await resp.json();

      if (resp.ok) {
        // Dispatch cart update event for theme drawer/header counter
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        // Redirect to cart
        window.location.href = '/cart';
      } else {
        throw new Error(data.description || 'Could not add items to cart.');
      }
    } catch (e) {
      console.error('[OCP Builder] Cart error:', e);
      if (msg) {
        msg.textContent = `⚠️ ${e.message} Please try again or contact support.`;
        msg.classList.remove('hidden');
      }
      if (btn) {
        btn.disabled    = false;
        btn.textContent = `Add to Cart — ${formatMoney(calculateTotal())}`;
      }
    }
  }

  // ─── Shared UI Builders ────────────────────────────────────────────────────
  // `usedVariantIds` (optional) marks variants that are already consumed in
  // another slot (used by the skill-badge step to enforce unique selection).
  // Used cards render in a dimmed state, can't be clicked, and show an
  // "In use" pill so the customer knows why it's unavailable.
  function buildProductGrid(products, type, selectedVariantId, usedVariantIds) {
    if (!products || products.length === 0) {
      return `<p class="no-products-msg">No products found in this collection. Add products to the "${type}" collection in your Shopify admin.</p>`;
    }
    const usedSet = new Set(
      (Array.isArray(usedVariantIds) ? usedVariantIds : [])
        .map(id => String(id))
    );
    return products.map(product => {
      const variant    = product.variants[0];
      const image      = product.images[0]?.src || '';
      const isSelected = selectedVariantId && variant.id === selectedVariantId;
      const isUsed     = usedSet.has(String(variant.id)) && !isSelected;
      // Out of stock = the variant isn't purchasable (sold out and not set to
      // "continue selling"). Flagged on THIS step so the customer is told here,
      // not at checkout. `available` comes straight from the products.json data.
      const isSoldOut  = variant.available === false && !isSelected;
      const isDisabled = isUsed || isSoldOut;
      const cardClasses = [
        'product-card',
        isSelected ? 'selected' : '',
        isUsed     ? 'used'     : '',
        isSoldOut  ? 'sold-out' : '',
      ].filter(Boolean).join(' ');
      const ariaLabel = [
        product.title,
        formatMoney(variant.price / 100),
        isSelected ? 'selected' : '',
        isUsed     ? 'already selected in another slot' : '',
        isSoldOut  ? 'sold out' : '',
      ].filter(Boolean).join(', ');
      return `
        <div class="${cardClasses}"
             data-type="${type}"
             data-variant-id="${variant.id}"
             data-price="${variant.price / 100}"
             data-title="${escapeAttr(product.title)}"
             data-image="${escapeAttr(image)}"
             data-product-handle="${product.handle}"
             ${isUsed ? 'data-used="true"' : ''}
             ${isSoldOut ? 'data-soldout="true"' : ''}
             role="button"
             tabindex="${isDisabled ? '-1' : '0'}"
             aria-disabled="${isDisabled ? 'true' : 'false'}"
             aria-label="${escapeAttr(ariaLabel)}">
          <div class="product-card-img-wrap">
            ${image
              ? `<img src="${image}" alt="${escapeAttr(product.title)}" loading="lazy" />`
              : '<div class="product-card-no-img">No Image</div>'
            }
            ${isSelected ? '<div class="selected-check" aria-hidden="true">✓</div>' : ''}
            ${isSoldOut
              ? '<div class="soldout-pill" aria-hidden="true">Sold out</div>'
              : (isUsed ? '<div class="used-pill" aria-hidden="true">In use</div>' : '')
            }
          </div>
          <div class="product-card-info">
            <span class="product-card-name">${product.title}</span>
            <span class="product-card-price">${formatMoney(variant.price / 100)}</span>
          </div>
        </div>
      `;
    }).join('');
  }

  function bindProductGrid(gridId, type, onSelect) {
    const grid = document.getElementById(gridId);
    if (!grid) return;

    const handler = function (e) {
      const card = e.target.closest(`.product-card[data-type="${type}"]`);
      if (!card) return;

      // Sold-out items can't be added. Tell the customer right here on the
      // selection step instead of letting it fail at checkout.
      if (card.dataset.soldout === 'true') {
        flashMessage(`"${card.dataset.title}" is sold out right now — please choose another.`);
        return;
      }

      // Silently ignore cards flagged as already-used in another slot. The
      // visual "In use" pill tells the customer why nothing happened; the
      // onSelect handler still runs its own duplicate check as a safety net.
      if (card.dataset.used === 'true') return;

      // Build a minimal product/variant object from data attrs
      const product = {
        title:  card.dataset.title,
        handle: card.dataset.productHandle,
        images: card.dataset.image ? [{ src: card.dataset.image }] : [],
        variants: [{
          id:    parseInt(card.dataset.variantId),
          price: Math.round(parseFloat(card.dataset.price) * 100),
        }],
      };
      const variant = product.variants[0];
      onSelect(product, variant);
    };

    grid.addEventListener('click', handler);
    grid.addEventListener('keydown', e => {
      if (e.key === 'Enter' || e.key === ' ') handler(e);
    });
  }

  function bindSearch(inputId, allProducts, gridId, type, onSelect) {
    const input = document.getElementById(inputId);
    if (!input) return;

    input.addEventListener('input', function () {
      const q      = this.value.toLowerCase().trim();
      const filtered = q
        ? allProducts.filter(p => p.title.toLowerCase().includes(q) ||
                                   (p.tags && p.tags.some(t => t.toLowerCase().includes(q))))
        : allProducts;

      const grid = document.getElementById(gridId);
      if (grid) {
        grid.innerHTML = buildProductGrid(filtered, type, null);
        bindProductGrid(gridId, type, onSelect);
      }
    });
  }

  // ─── Paginated Grid ────────────────────────────────────────────────────────
  // Drop-in replacement for the old `bindSearch` + `bindProductGrid` combo on
  // steps that can have long lists of products (ranks, skill badges, ID/NG
  // badges). Renders a configurable number of items per page with a prev/next
  // pager below the grid, and keeps the search box working against the full
  // unfiltered collection so customers can jump to a match without paging
  // through 10+ screens on mobile.
  //
  // Search filter + current page are preserved at module scope across
  // re-renders (picking a badge re-renders the step, but we don't want the
  // user's in-progress search or page number to reset).
  //
  // opts = {
  //   gridId, pagerId, searchInputId, // DOM ids (pager/search optional)
  //   allProducts,                    // unfiltered product list
  //   type,                           // 'rank' | 'badge' | 'idbadge'
  //   onSelect,                       // click handler (product, variant) => void
  //   selectedVariantId,              // optional: variant to mark as selected
  //   focusVariantId,                 // optional: on fresh init, jump to the
  //                                   //   page containing this variant so the
  //                                   //   user's current pick stays visible
  //   usedVariantIds,                 // optional: variant ids already taken by
  //                                   //   other slots (renders those cards as
  //                                   //   disabled/"in use" — used by skill
  //                                   //   badges to enforce unique picks)
  // }
  function setupPaginatedGrid(opts) {
    const {
      gridId,
      pagerId,
      searchInputId,
      allProducts,
      type,
      onSelect,
      selectedVariantId = null,
      focusVariantId    = null,
      usedVariantIds    = [],
    } = opts;

    // Fewer per page on phones to cut down on scrolling; more on desktop.
    const isMobile = !!(window.matchMedia && window.matchMedia('(max-width: 600px)').matches);
    const pageSize = isMobile
      ? (config.gridPageSizeMobile || 4)
      : (config.gridPageSize || 8);

    // Pull (or seed) the persistent state for this grid.
    const stateForGrid = gridPageState[gridId] || (gridPageState[gridId] = {
      query: '',
      page:  1,
    });

    function currentFiltered() {
      const q = stateForGrid.query;
      if (!q) return allProducts;
      return allProducts.filter(p =>
        p.title.toLowerCase().includes(q) ||
        (Array.isArray(p.tags) && p.tags.some(t => t.toLowerCase().includes(q)))
      );
    }

    // On the very first render for this grid, if the user already has a
    // selection (e.g. returning to the rank step), jump to the page that
    // contains it so the "selected" checkmark is visible without hunting.
    // Only runs when state is fresh (page still at 1 and no query).
    function maybeJumpToSelection(filtered) {
      if (!focusVariantId || stateForGrid._focusApplied) return;
      const idx = filtered.findIndex(p =>
        Array.isArray(p.variants) &&
        p.variants.some(v => String(v.id) === String(focusVariantId))
      );
      if (idx >= 0) {
        stateForGrid.page = Math.floor(idx / pageSize) + 1;
      }
      stateForGrid._focusApplied = true;
    }

    function render() {
      const filtered   = currentFiltered();
      const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));

      // Clamp page if filtering shrank the result set.
      if (stateForGrid.page > totalPages) stateForGrid.page = totalPages;
      if (stateForGrid.page < 1)          stateForGrid.page = 1;

      maybeJumpToSelection(filtered);

      const start  = (stateForGrid.page - 1) * pageSize;
      const slice  = filtered.slice(start, start + pageSize);

      const grid = document.getElementById(gridId);
      if (grid) {
        if (filtered.length === 0 && stateForGrid.query) {
          // Active search with no matches — show a search-specific empty
          // state rather than buildProductGrid's "add products to the
          // collection" message, which would be misleading here.
          grid.innerHTML = `
            <p class="no-products-msg">
              No matches for “${escapeAttr(stateForGrid.query)}”.
              Try a different search term.
            </p>`;
        } else {
          grid.innerHTML = buildProductGrid(slice, type, selectedVariantId, usedVariantIds);
          bindProductGrid(gridId, type, onSelect);
        }
      }

      const pager = pagerId ? document.getElementById(pagerId) : null;
      if (pager) {
        pager.innerHTML = buildPagerHtml(
          stateForGrid.page,
          totalPages,
          filtered.length
        );
        bindPagerButtons(pager, (delta) => {
          stateForGrid.page = Math.max(1, Math.min(totalPages, stateForGrid.page + delta));
          render();
        });
      }
    }

    // Wire search — filter against the full product list, reset to page 1
    // whenever the query changes. Restore any previously-typed query so the
    // filter survives a re-render caused by selecting a card.
    if (searchInputId) {
      const input = document.getElementById(searchInputId);
      if (input) {
        if (stateForGrid.query && input.value !== stateForGrid.query) {
          input.value = stateForGrid.query;
        }
        input.addEventListener('input', function () {
          const next = this.value.toLowerCase().trim();
          if (next === stateForGrid.query) return;
          stateForGrid.query = next;
          stateForGrid.page  = 1;
          render();
        });
      }
    }

    render();
  }

  // Pager HTML — a prev button, a status readout, and a next button. Hidden
  // (via a single-page class) when there's only one page of results so it
  // doesn't take up vertical space unnecessarily on small lists.
  function buildPagerHtml(page, totalPages, totalItems) {
    const prevDisabled = page <= 1 ? 'disabled' : '';
    const nextDisabled = page >= totalPages ? 'disabled' : '';
    const singlePage   = totalPages <= 1 ? 'product-pager--single' : '';
    const itemLabel    = totalItems === 1 ? 'item' : 'items';
    const countText    = totalItems === 0
      ? 'No matches'
      : `Page ${page} of ${totalPages} · ${totalItems} ${itemLabel}`;

    return `
      <div class="product-pager ${singlePage}" role="navigation" aria-label="Product grid pagination">
        <button type="button" class="pager-btn pager-prev" data-pager-delta="-1" ${prevDisabled} aria-label="Previous page">← Prev</button>
        <span class="pager-status" aria-live="polite">${countText}</span>
        <button type="button" class="pager-btn pager-next" data-pager-delta="1" ${nextDisabled} aria-label="Next page">Next →</button>
      </div>
    `;
  }

  function bindPagerButtons(container, onDelta) {
    container.querySelectorAll('.pager-btn[data-pager-delta]').forEach(btn => {
      btn.addEventListener('click', function () {
        if (this.disabled) return;
        onDelta(parseInt(this.dataset.pagerDelta, 10) || 0);
      });
    });
  }

  function buildSlotCard(sel, idx, type, layout) {
    const positionName = (type === 'badge' && layout)
      ? positionForSlot(layout, idx)
      : null;

    const headerLabel = type === 'badge'
      ? (positionName
          ? `Slot ${idx + 1} of ${layout.slots} — ${positionName}`
          : `Skill Badge Slot ${idx + 1}`)
      : `ID/NG Badge ${idx + 1}`;

    if (!sel) {
      return `
        <div class="badge-slot empty" data-slot="${idx}" data-slot-type="${type}">
          <div class="slot-number">${idx + 1}</div>
          <div class="slot-body">
            <span class="slot-position">${headerLabel}</span>
            <span class="slot-empty-msg">Search and click a badge below</span>
          </div>
        </div>
      `;
    }

    return `
      <div class="badge-slot filled" data-slot="${idx}" data-slot-type="${type}">
        ${sel.image
          ? `<img src="${sel.image}" alt="${escapeAttr(sel.title)}" class="slot-thumb" />`
          : '<div class="slot-thumb-placeholder"></div>'
        }
        <div class="slot-body">
          <span class="slot-position">${headerLabel}</span>
          <span class="slot-title">${sel.title}</span>
          <span class="slot-price">${formatMoney(sel.price)}</span>
          <span class="sew-inline-included">
            <span class="sew-inline-included-check" aria-hidden="true">✓</span>
            Sewing included (+${formatMoney(config.sewingFee)} fee)
          </span>
        </div>
        <button class="slot-remove-btn" data-slot="${idx}" data-slot-type="${type}" aria-label="Remove ${headerLabel}">✕</button>
      </div>
    `;
  }

  function bindSlotRemoveButtons(type) {
    document.querySelectorAll(`.slot-remove-btn[data-slot-type="${type}"]`).forEach(btn => {
      btn.addEventListener('click', function () {
        const idx = parseInt(this.dataset.slot);
        if (type === 'badge') {
          state.badges.selections[idx] = null;
        } else {
          state.idBadges.selections.splice(idx, 1);
        }
        renderCurrentStep();
      });
    });
  }

  function buildLayoutIcon(layout) {
    const rows = layout.grid.map(row => `
      <div class="li-row" style="grid-template-columns: repeat(${row.length}, 1fr);">
        ${row.map(n => `<div class="li-cell">${n}</div>`).join('')}
      </div>
    `).join('');
    return `<div class="li-wrap">${rows}</div>`;
  }

  // ─── Badge Layout Schematic ────────────────────────────────────────────────
  // Compact preview showing just the skill-badge zone with a U.S. Army tape
  // beneath it as a spatial anchor. No jacket outline, pockets, or name tape —
  // this keeps the preview clean and readable on both desktop and mobile.
  //
  // `selections` is an optional array of { title, image, sewOn } in slot order;
  // when provided, each slot is filled with the badge's name so the sewer can
  // see exactly which badge goes into which spot.
  function buildJacketSchematic(layout, selections) {
    if (!layout) return '';

    const rows      = layout.grid;
    const rowCount  = rows.length;

    // Canvas geometry — viewBox scales responsively via CSS.
    const W         = 320;
    const padX      = 24;
    const labelY    = 18;   // SKILL BADGES header baseline
    const zoneY     = 34;   // top of badge zone
    const zoneX     = padX;
    const zoneW     = W - padX * 2;
    const slotH     = 54;
    const rowGap    = 6;
    const colGap    = 6;
    const zoneH     = rowCount * slotH + (rowCount - 1) * rowGap;
    const tapeGap   = 18;
    const tapeW     = 170;
    const tapeH     = 26;
    const tapeX     = (W - tapeW) / 2;
    const tapeY     = zoneY + zoneH + tapeGap;
    const H         = tapeY + tapeH + 14;

    // Compute slot rectangles for every slot number.
    const slotBoxes = {};
    rows.forEach((row, r) => {
      const colCount = row.length;
      const colWidth = (zoneW - colGap * (colCount - 1)) / colCount;
      row.forEach((slotNum, c) => {
        const x = zoneX + c * (colWidth + colGap);
        const y = zoneY + r * (slotH + rowGap);
        slotBoxes[slotNum] = { x, y, w: colWidth, h: slotH };
      });
    });

    const slotSvg = Object.entries(slotBoxes).map(([slotNum, b]) => {
      const n            = parseInt(slotNum);
      const sel          = selections && selections[n - 1];
      const position     = (layout.positions && layout.positions[n]) || `Slot ${n}`;
      const filled       = !!sel;
      const bgFill       = filled ? '#E6EFD9' : '#F5F0DC';
      const strokeColor  = filled ? '#4A5A2D' : '#8F7A4A';
      const badgeTitle   = sel ? truncateForSvg(sel.title, Math.max(10, Math.floor(b.w / 6.5))) : '';

      return `
        <g class="schematic-slot">
          <rect x="${b.x}" y="${b.y}" width="${b.w}" height="${b.h}"
                fill="${bgFill}" stroke="${strokeColor}" stroke-width="1.5"
                rx="5" ry="5" />
          <text x="${b.x + b.w / 2}" y="${b.y + 16}"
                text-anchor="middle" font-size="11" font-weight="700"
                fill="#4A5A2D" font-family="system-ui, sans-serif">
            ${n}
          </text>
          <text x="${b.x + b.w / 2}" y="${b.y + b.h - 8}"
                text-anchor="middle" font-size="9" font-weight="600"
                fill="#4A5A2D" font-family="system-ui, sans-serif">
            ${escapeXml(position)}
          </text>
          ${filled ? `
            <text x="${b.x + b.w / 2}" y="${b.y + b.h / 2 + 3}"
                  text-anchor="middle" font-size="9" font-weight="500"
                  fill="#2A3419" font-family="system-ui, sans-serif">
              ${escapeXml(badgeTitle)}
            </text>
          ` : ''}
        </g>
      `;
    }).join('');

    return `
      <svg class="jacket-schematic-svg" viewBox="0 0 ${W} ${H}"
           xmlns="http://www.w3.org/2000/svg" role="img"
           aria-label="Badge layout preview: ${escapeXml(layout.label)}">
        <!-- Skill Badges header -->
        <text x="${W / 2}" y="${labelY}"
              text-anchor="middle" font-size="11" font-weight="700"
              fill="#4A5A2D" font-family="system-ui, sans-serif"
              letter-spacing="1.4">
          SKILL BADGES
        </text>

        ${slotSvg}

        <!-- U.S. Army tape anchor -->
        <rect x="${tapeX}" y="${tapeY}" width="${tapeW}" height="${tapeH}"
              fill="#E6D5A8" stroke="#6B5E3E" stroke-width="1.2"
              rx="3" ry="3" />
        <text x="${W / 2}" y="${tapeY + tapeH / 2 + 4}"
              text-anchor="middle" font-size="11" font-weight="700"
              fill="#4A5A2D" font-family="system-ui, sans-serif"
              letter-spacing="2">
          U.S. ARMY
        </text>
      </svg>
    `;
  }

  function truncateForSvg(text, max) {
    const s = String(text);
    if (s.length <= max) return s;
    return s.slice(0, Math.max(1, max - 1)) + '…';
  }

  function escapeXml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  }

  // Labels used for screen-reader step announcements after prev/next nav.
  const STEP_NAMES = {
    1: 'Pick Jacket',
    2: 'Jacket Fit',
    3: 'Name Tapes',
    4: 'Rank',
    5: 'Skill Badges',
    6: 'ID and National Guard Badges',
    7: 'Review Order',
  };

  function announceStepChange() {
    const step = state.currentStep;
    announce(`Step ${step} of ${state.totalSteps}: ${STEP_NAMES[step] || ''}`);
  }

  // ─── Navigation ────────────────────────────────────────────────────────────
  function attachNavListeners() {
    const prevBtn = document.getElementById('nav-prev');
    const nextBtn = document.getElementById('nav-next');

    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        if (state.currentStep > 1) {
          state.currentStep--;
          renderCurrentStep();
          announceStepChange();
          focusStepHeading();
        }
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        if (!isStepValid(state.currentStep)) return;
        state.currentStep++;
        renderCurrentStep();
        announceStepChange();
        focusStepHeading();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }
  }

  function updateNavButtons() {
    const prevBtn = document.getElementById('nav-prev');
    const nextBtn = document.getElementById('nav-next');
    if (!prevBtn || !nextBtn) return;

    const isReview = state.currentStep === 7;

    prevBtn.style.display  = (state.currentStep > 1 && !isReview) ? 'inline-flex' : 'none';
    nextBtn.style.display  = isReview ? 'none' : 'inline-flex';
    nextBtn.disabled       = !isStepValid(state.currentStep);

    if (state.currentStep === state.totalSteps) {
      nextBtn.textContent = 'Review My Order →';
    } else {
      nextBtn.textContent = 'Next Step →';
    }
  }

  function isStepValid(step) {
    switch (step) {
      case 1:
        // Must have picked one of the two jacket products AND that product must
        // actually be purchasable. Blocking here means a customer can never
        // build an entire uniform on a jacket we can't ship.
        return !!state.jacketChoice
          && !!getActiveJacketProduct()
          && isJacketInStock(getActiveJacketProduct());

      case 2:
        return !!state.jacket.variantId;

      case 3:
        // Must have a valid name AND the tape product must have actually loaded,
        // otherwise the tape (and its $19.90) will be silently dropped from the cart.
        return state.tape.customName.trim().length > 0 && !!state.tape.variantId;

      case 4:
        return !!state.rank.variantId;

      case 5:
        if (state.badges.wantBadges === null) return false;
        if (!state.badges.wantBadges) return true;
        if (!state.badges.layout) return false;
        return state.badges.selections.every(s => s !== null);

      case 6:
        if (state.idBadges.wantBadges === null) return false;
        if (!state.idBadges.wantBadges) return true;
        return state.idBadges.selections.length === state.idBadges.count;

      default:
        return true;
    }
  }

  // ─── Progress Bar ──────────────────────────────────────────────────────────
  function updateProgressBar() {
    const step        = Math.min(state.currentStep, state.totalSteps);
    const pct         = ((step - 1) / (state.totalSteps - 1)) * 100;
    const fill        = document.getElementById('progress-fill');
    const track       = document.querySelector('.progress-track');
    const stepLabels  = document.querySelectorAll('.progress-step');

    if (fill) fill.style.width = `${pct}%`;

    // Mark the progress track as a proper ARIA progressbar so screen readers
    // announce "Step X of 5" instead of reading the visual dots as generic
    // divs. We do this in JS (rather than the Liquid template) so the markup
    // is guaranteed to stay in sync with the step count if it ever changes.
    const stepMap   = {
      1: 'Pick Jacket',
      2: 'Jacket Fit',
      3: 'Name Tapes',
      4: 'Rank',
      5: 'Skill Badges',
      6: 'ID/NG Badges',
      7: 'Review Order',
    };
    const stepName = stepMap[step] || '';

    if (track) {
      track.setAttribute('role',           'progressbar');
      track.setAttribute('aria-valuemin',  '1');
      track.setAttribute('aria-valuemax',  String(state.totalSteps));
      track.setAttribute('aria-valuenow',  String(step));
      track.setAttribute('aria-valuetext', `Step ${step} of ${state.totalSteps}: ${stepName}`);
      track.setAttribute('aria-label',     'Builder progress');
    }

    stepLabels.forEach((el, i) => {
      el.classList.toggle('is-completed', i + 1 < step);
      el.classList.toggle('is-active',    i + 1 === step);
      // Mark the current step for assistive tech; completed steps don't need
      // aria-current (they're in the past).
      if (i + 1 === step) el.setAttribute('aria-current', 'step');
      else                el.removeAttribute('aria-current');
    });

    const stepTitle = document.getElementById('current-step-label');
    if (stepTitle) stepTitle.textContent = `Step ${step} of ${state.totalSteps}: ${stepName}`;
  }

  // ─── Screen Reader Announcements ────────────────────────────────────────────
  // A visually hidden live region inserted once, updated whenever the step
  // changes. `aria-live: polite` means the message is queued politely after
  // the current screen-reader utterance finishes, so it doesn't interrupt.
  function ensureLiveRegion() {
    let lr = document.getElementById('ocp-builder-announcer');
    if (!lr) {
      lr = document.createElement('div');
      lr.id = 'ocp-builder-announcer';
      lr.className = 'sr-only';
      lr.setAttribute('role',        'status');
      lr.setAttribute('aria-live',   'polite');
      lr.setAttribute('aria-atomic', 'true');
      const root = document.getElementById('ocp-builder');
      if (root) root.appendChild(lr);
    }
    return lr;
  }

  function announce(text) {
    const lr = ensureLiveRegion();
    // Clearing then setting text on the next tick forces screen readers to
    // re-announce even if the message text is the same as before.
    lr.textContent = '';
    setTimeout(() => { lr.textContent = text; }, 10);
  }

  // After a step transition, move keyboard focus to the step's <h2> heading
  // so screen reader users hear the new step context and keyboard users pick
  // up at the top of the new content. `tabindex="-1"` makes the heading
  // programmatically focusable without adding it to the normal tab order.
  function focusStepHeading() {
    setTimeout(() => {
      const heading = document.querySelector('#builder-content .step-title');
      if (!heading) return;
      heading.setAttribute('tabindex', '-1');
      try { heading.focus({ preventScroll: true }); } catch (e) { heading.focus(); }
    }, 0);
  }

  // ─── Utilities ─────────────────────────────────────────────────────────────
  function formatMoney(dollars) {
    return `$${parseFloat(dollars).toFixed(2)}`;
  }

  function escapeAttr(str) {
    return String(str).replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // Sewing for the tape step is bundled into the Name Tape Set product price,
  // so no sewing fee is added for tapes. Ranks and badges each get one sewing
  // service fee added on top of the product price.
  function calculateTotal() {
    let total = (state.jacket.price || 0) + (state.tape.price || 0);
    if (state.rank.variantId) {
      total += state.rank.price + config.sewingFee;
    }
    state.badges.selections.filter(Boolean).forEach(b => {
      total += b.price + config.sewingFee;
    });
    state.idBadges.selections.forEach(b => {
      total += b.price + config.sewingFee;
    });
    return total;
  }

  function showLoading(show) {
    const loader  = document.getElementById('builder-loading');
    const content = document.getElementById('builder-content');
    const nav     = document.getElementById('builder-nav');
    if (loader)  loader.style.display  = show ? 'flex'   : 'none';
    if (content) content.style.display = show ? 'none'   : 'block';
    if (nav)     nav.style.display     = show ? 'none'   : 'flex';
  }

  function flashMessage(msg) {
    const existing = document.getElementById('flash-msg');
    if (existing) existing.remove();
    const el = document.createElement('div');
    el.id            = 'flash-msg';
    el.className     = 'flash-message';
    el.setAttribute('role', 'alert');
    el.setAttribute('aria-live', 'assertive');
    el.setAttribute('aria-atomic', 'true');
    el.textContent   = msg;
    document.getElementById('builder-content').prepend(el);
    setTimeout(() => el.remove(), 4000);
  }

  // ─── Boot ──────────────────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
